import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/UI/base/Button';
import { TreatmentPlanTemplateCard } from '../components/Configuration/TreatmentPlanTemplateCard';
import { DeleteTemplateModal } from '../components/Configuration/DeleteTemplateModal';
import { useTreatmentPlanTemplates } from '../hooks/useTreatmentPlanTemplates';
import { useLocations } from '../hooks/useLocations';
import { buildDuplicateRequest } from '../services/templateMappers';
import type { TreatmentPlanTemplateResponse } from '../api/generated';
import { useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';
import ROUTES from '../routes/routeConstants';
import { getErrorMessage } from '../utils/errorMessageUtils';
import styles from './ConfigurationPage.module.css';

export const ConfigurationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { templates, isLoading, loadError, createTemplate, deleteTemplate } =
    useTreatmentPlanTemplates();
  const { locationsById } = useLocations();
  const [deleteModalTemplate, setDeleteModalTemplate] =
    useState<TreatmentPlanTemplateResponse | null>(null);

  const handleNewTemplate = () => {
    navigate(`/${ROUTES.configuration}/treatment-plans/new`);
  };

  const handleEditTemplate = (template: TreatmentPlanTemplateResponse) => {
    navigate(`/${ROUTES.configuration}/treatment-plans/${template.id}`);
  };

  const handleDeleteTemplate = (template: TreatmentPlanTemplateResponse) => {
    setDeleteModalTemplate(template);
  };

  const handleDuplicateTemplate = async (template: TreatmentPlanTemplateResponse) => {
    const newName = t('config-duplicate-name', { name: template.name });
    try {
      await createTemplate(buildDuplicateRequest(template, newName));
    } catch (e) {
      dispatch(showError({ message: getErrorMessage(e, t('config-create-error')) }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalTemplate) return;
    const idToDelete = deleteModalTemplate.id;
    setDeleteModalTemplate(null);
    try {
      await deleteTemplate(idToDelete);
    } catch (e) {
      dispatch(showError({ message: getErrorMessage(e, t('config-delete-error')) }));
    }
  };

  const showEmptyState = !isLoading && !loadError && templates.length === 0;

  return (
    <main className={styles.page}>
      <div className={styles.titleRow}>
        <h1 className={styles.heading}>{t('config-page-title')}</h1>
        <div className={styles.titleActions}>
          <Button
            type="secondary"
            onClick={() => void navigate(`/${ROUTES.configurationInterventions}`)}
          >
            {t('config-tab-interventions')}
          </Button>
          <Button type="primary" onClick={handleNewTemplate}>
            {t('config-new-treatment-plan')}
          </Button>
        </div>
      </div>

      {isLoading && templates.length === 0 && (
        <p className={styles.emptyState}>{t('config-loading-templates')}</p>
      )}

      {loadError && <p className={styles.emptyState}>{t('config-load-error')}</p>}

      {showEmptyState && (
        <p className={styles.emptyState}>{t('config-no-treatment-plans')}</p>
      )}

      {templates.length > 0 && (
        <div className={styles.cardGrid}>
          {templates.map((template) => (
            <TreatmentPlanTemplateCard
              key={template.id}
              template={template}
              locationsById={locationsById}
              onEdit={() => handleEditTemplate(template)}
              onDelete={() => handleDeleteTemplate(template)}
              onDuplicate={() => void handleDuplicateTemplate(template)}
            />
          ))}
        </div>
      )}

      {deleteModalTemplate && (
        <DeleteTemplateModal
          templateName={deleteModalTemplate.name}
          onConfirm={() => void handleConfirmDelete()}
          onClose={() => setDeleteModalTemplate(null)}
        />
      )}
    </main>
  );
};
